import { z } from "zod/v4";
import {
  CameraSubject,
  ClientApiFeaturePathParams,
  CollectionResponse,
  defineResource,
  defineEndpoint,
} from "@visioni/a3s-client-api";
import { isoDatetimeToDateString, isoDate, stringToInt } from "@visioni/a3s-codecs";
import { FEATURE_NAME } from "../constants";

export type ListClipsPathParams = ClientApiFeaturePathParams;

/**
 * Query parameters for list clips request.
 */
export const ListClipsQueryParams = z.object({
  startTime: isoDatetimeToDateString,
  endTime: isoDatetimeToDateString,
  limit: stringToInt.pipe(z.int().positive().max(100)).optional(),
  cursor: z.string().optional(),
});

export type ListClipsQueryParams = z.output<typeof ListClipsQueryParams>;

export const ClipVideo = z.object({
  url: z.string(),
});

export type ClipVideo = z.output<typeof ClipVideo>;

export const ClipItem = z.object({
  subject: CameraSubject,
  startTime: z.string(),
  durationMs: z.number(),
  /** @deprecated Use video.url instead. Kept for backwards compatibility with deployed apps. */
  videoUrl: z.string().optional(),
  video: ClipVideo.optional(),
  thumbnailUrl: z.string().optional(),
});

export type ClipItem = z.output<typeof ClipItem>;

export const ListClipsResponse = CollectionResponse(ClipItem);

export type ListClipsResponse = z.output<typeof ListClipsResponse>;

// ==================== Resources & Endpoints ====================

export const clipsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "clips",
});

export const listClipsEndpoint = defineEndpoint(clipsResource, {
  responseSchema: ListClipsResponse,
  querySchema: ListClipsQueryParams,
});

// ==================== Hourly Counts ====================

export const CountClipsByHourQueryParams = z.object({
  date: isoDate,
  timezone: z.string(),
});

export type CountClipsByHourQueryParams = z.output<typeof CountClipsByHourQueryParams>;

export const HourCount = z.object({
  hour: z.number(),
  count: z.number(),
  totalDurationMs: z.number(),
});

export type HourCount = z.output<typeof HourCount>;

export const CountClipsByHourResponse = z.object({
  data: z.array(HourCount),
});

export type CountClipsByHourResponse = z.output<typeof CountClipsByHourResponse>;

export const clipCountsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "clips/counts",
});

export const countClipsByHourEndpoint = defineEndpoint(clipCountsResource, {
  responseSchema: CountClipsByHourResponse,
  querySchema: CountClipsByHourQueryParams,
});
